import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  ChartBarIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import studentAuthService from '../../../services/studentAuth';

const StudentTests = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    averageScore: 0,
  });

  useEffect(() => {
    loadTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTests = async () => {
    try {
      setLoading(true);
      const data = await studentAuthService.getTests(0, 100);
      setTests(data);
      calculateStats(data);
    } catch (error) {
      console.error('Failed to load tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    // Exclude attempts whose results are hidden by the test creator - their
    // score/pass-fail is unknown to the student, so they don't count in stats.
    const visible = data.filter((test) => !test.results_hidden);
    const total = visible.length;
    const passed = visible.filter((test) => test.is_passed).length;
    const failed = total - passed;
    const averageScore =
      total > 0
        ? Math.round(
            visible.reduce((sum, test) => sum + test.score_percentage, 0) / total
          )
        : 0;

    setStats({ total, passed, failed, averageScore });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 75) return 'text-green-400';
    if (percentage >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBgColor = (percentage) => {
    if (percentage >= 75) return 'bg-green-500/20 border-green-500/50';
    if (percentage >= 50) return 'bg-yellow-500/20 border-yellow-500/50';
    return 'bg-red-500/20 border-red-500/50';
  };

  const getStatBorderColor = (label) => {
    if (label === 'Total') return 'hover:border-blue-500/50';
    if (label === 'Passed') return 'hover:border-green-500/50';
    if (label === 'Failed') return 'hover:border-red-500/50';
    return 'hover:border-purple-500/50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/student/dashboard')}
                className="p-1.5 sm:p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">My Test Results</h1>
                <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">
                  Track your performance
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 ${getStatBorderColor('Total')} transition-all hover:scale-105`}>
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">Total Tests</p>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats.total}</p>
          </div>

          <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 ${getStatBorderColor('Passed')} transition-all hover:scale-105`}>
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">Passed</p>
            <p className="text-2xl sm:text-3xl font-bold text-green-400">{stats.passed}</p>
          </div>

          <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 ${getStatBorderColor('Failed')} transition-all hover:scale-105`}>
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <XCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">Failed</p>
            <p className="text-2xl sm:text-3xl font-bold text-red-400">{stats.failed}</p>
          </div>

          <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700/50 ${getStatBorderColor('Average')} transition-all hover:scale-105`}>
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <TrophyIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mb-1">Avg Score</p>
            <p className={`text-2xl sm:text-3xl font-bold ${getScoreColor(stats.averageScore)}`}>
              {stats.averageScore}%
            </p>
          </div>
        </div>

        {/* Test Records */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50">
          <div className="p-4 sm:p-6 border-b border-gray-700/50">
            <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              </div>
              Test History
            </h3>
          </div>
          {tests.length > 0 ? (
            <div className="divide-y divide-gray-700/50">
              {tests.map((test, index) => (
                <div
                  key={index}
                  className="p-4 sm:p-6 hover:bg-gray-700/30 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex-shrink-0 border border-blue-500/30">
                        <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-semibold text-white mb-1 truncate">
                          {test.test_title}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-400">
                          <div className="flex items-center gap-1">
                            <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>{formatDate(test.submitted_at)}</span>
                          </div>
                          {!test.results_hidden && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span>
                                {test.correct_answers}/{test.total_questions} correct
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 ml-12 sm:ml-0">
                      {test.results_hidden ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full bg-gray-700/50 text-gray-300 border border-gray-600">
                          Results not available
                        </span>
                      ) : (
                        <>
                          <div className={`px-3 py-2 rounded-lg border-2 ${getScoreBgColor(test.score_percentage)}`}>
                            <p className="text-xs text-gray-300 mb-0.5">Score</p>
                            <p className={`text-lg sm:text-xl font-bold ${getScoreColor(test.score_percentage)}`}>
                              {test.score_percentage}%
                            </p>
                          </div>
                          {test.is_passed ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full bg-green-900/30 text-green-300 border border-green-700">
                              <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                              Passed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full bg-red-900/30 text-red-300 border border-red-700">
                              <XCircleIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                              Failed
                            </span>
                          )}
                        </>
                      )}
                      <a
                        href={`/test/${test.test_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all hover:scale-105 text-xs sm:text-sm font-medium inline-flex items-center gap-1"
                      >
                        View Test
                        <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-20">
              <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full p-6 sm:p-8 w-fit mx-auto mb-4 border border-blue-500/20">
                <DocumentTextIcon className="h-12 w-12 sm:h-16 sm:w-16 text-blue-400" />
              </div>
              <p className="text-gray-400 text-base sm:text-lg font-medium mb-2">No test records found</p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Your test results will appear here after you complete tests
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentTests;
